import { CheckoutItem } from '@ancon/wildcat-types'
import { CheckoutRequestItem } from '@ancon/wildcat-utils/checkout/types'

import { CapacityConflictLine, CheckoutRequestedItemDetails } from '../types'

/**
 * The cart that was attempted, as the allocator needs to see it.
 *
 * The client only ever sends variant ids while the error names product ids, so the
 * two have to be joined. The checkout we already hold is the better source - it
 * carries the product id and display name for everything currently in the cart -
 * and the remembered details cover what isn't in it yet: the item being added, and
 * everything on the create path, where no checkout exists at all.
 *
 * An item neither source knows keeps its place in the list rather than being
 * dropped, with a null product id, so the guest still sees their whole cart and the
 * unresolved line is visible as such instead of silently looking uncapped.
 */
export default function getCapacityConflictLines(
  requestedItems: CheckoutRequestItem[],
  checkoutItems: CheckoutItem[],
  itemDetails: Record<string, CheckoutRequestedItemDetails>,
): CapacityConflictLine[] {
  const checkoutItemsByVariantId = checkoutItems.reduce<
    Record<string, CheckoutItem>
  >((items, item) => ({ ...items, [item.product.variantId]: item }), {})

  return requestedItems.reduce<CapacityConflictLine[]>((lines, item, index) => {
    const { variantId } = item

    // Wallet top-ups and gift cards are sent without a variant id and can't take
    // part in a capacity conflict.
    if (!variantId) {
      return lines
    }

    const product = checkoutItemsByVariantId[variantId]?.product
    const details = itemDetails[variantId]
    const name = product
      ? (product.hasVariants && product.variantName) || product.name
      : details?.name

    lines.push({
      // Request items carry no id of their own, and the same variant can appear in
      // several lines with different modifications, so position identifies it.
      id: `${variantId}-${index}`,
      variantId,
      productId: product?.id ?? details?.productId ?? null,
      name: name ?? '',
      quantity: item.quantity,
    })

    return lines
  }, [])
}
